import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Copy, ExternalLink, CheckCircle } from 'lucide-react';

const TxDetailModal = ({ transaction, isOpen, onClose }) => {
  if (!transaction) return null;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const handleViewExplorer = () => {
    const explorerUrl = `https://explorer.dotprotocol.org/tx/${transaction.tx}`;
    window.open(explorerUrl, '_blank');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Transaction Details</span>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle className="w-3 h-3 mr-1" />
              Verified
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Transaction Hash */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">Transaction Hash</label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <code className="flex-1 font-mono text-sm">{transaction.tx}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(transaction.tx)}
                className="shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Date & Time */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">Date & Time</label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-sm">{formatDate(transaction.date)}</span>
            </div>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">Amount</label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-lg font-semibold text-green-600">
                ${transaction.amount_usd.toLocaleString()}
              </span>
            </div>
          </div>

          {/* From Address */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">From</label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <span className="flex-1 text-sm">{transaction.from}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(transaction.from)}
                className="shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">Status</label>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800 border-green-200">
                <CheckCircle className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleViewExplorer}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View on Dot Protocol Explorer
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>

          {/* Verification Info */}
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">How to Verify</h4>
            <p className="text-sm text-blue-800">
              Click "View on Dot Protocol Explorer" to see the on-chain record. 
              All transactions listed here are publicly verifiable on the Dot Protocol chain 
              (Chain ID 1546) using the Kibouor Foundation public address: 
              <code className="ml-1 font-mono text-xs">0xa3b1f9c4d2e6b7f8c9a0d4e5f6b7c8d9e0a1b2c3</code>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TxDetailModal;
